import React, { useContext } from 'react'
import { customVariableDataInterface } from '../../../../../../models/axiosModel';
import { Autocomplete, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import styles from '../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowForm.module.scss'
import globalStyles from "../../../../../../assets/styles/global.module.scss"
import dashboardStyles from '../../../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss'
import popupStyles from "../../../../../../assets/styles/componentStyles/globalPopupStyles.module.scss"
import autoCompleteStyles from './../../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import whatsAppCampaignStyles from "../../../../../../assets/styles/componentStyles/whatsappCampaignStyles/whatsappCampaignStyle.module.scss"
import clsx from "clsx";
import Required from '../../../../../utility/required';
import { useFlowBuilderStore } from '../../../../../../store/flowBuilderStore';
import { useShallow } from 'zustand/react/shallow';
import { isCurrentViewVersionPublished } from '../../../utilityFunctions';
import { WorkflowContext, WorkflowContextPayload } from '../../../../../../context/workflowContext';
import RenderCustomOption from '../../../../../global/autocomplete/renderCustomOption';
import { filterAutocompleteOptionsWithException } from '../../../../../../utils/constants';
import { FlowBuilderContext, FlowBuilderContextPayload } from '../../../../../../context/flowBuilderContext';
import GlobalTooltip from '../../../../../utility/tooltip/globalTooltip';
import { emptyNewVariableFieldObj } from '../../../flowBuilderConstants';
import { retrieveContactActionData, retrieveContactActionVariableData } from '../../../../../../models/flowBuilderTypes';

interface RetrieveContactVariableConfigProps {
    actionData: retrieveContactActionData,
    stepAction: any,
}

type RetrieveContactFieldType = "variable" | "fallback value";

interface RetrieveContactFieldUpdateType {
    type: RetrieveContactFieldType,
    value: customVariableDataInterface | null | string,
    variable: retrieveContactActionVariableData,
}

const RetrieveContactVariableConfig: React.FC<RetrieveContactVariableConfigProps> = (props) => {
    
    const { actionData, stepAction } = props;

    const { currentActiveStaticWorkflow } = useContext(WorkflowContext) as WorkflowContextPayload
    const { onChangeCurrentActiveNodeConfig, nodes, currentActiveNodeConfig } = useContext(FlowBuilderContext) as FlowBuilderContextPayload
    
    const { customVariableList, setFlagDataCollectionPopup, setFlagUpdateFieldData, setExistingCustomVariablePayload, existingCustomVariablePayload, 
        setFlagDeleteData } = useFlowBuilderStore(useShallow((state) => ({
        customVariableList: state.customVariableList,
        setFlagDataCollectionPopup: state.setFlagDataCollectionPopup,
        setFlagUpdateFieldData: state.setFlagUpdateFieldData,
        setExistingCustomVariablePayload: state.setExistingCustomVariablePayload,
        existingCustomVariablePayload: state.existingCustomVariablePayload,
        setFlagDeleteData: state.setFlagDeleteData
    })))

    const handleEditFieldData = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, option: customVariableDataInterface) => {
        event.stopPropagation();
        setFlagDataCollectionPopup(true);
        setFlagUpdateFieldData(true);
        setExistingCustomVariablePayload({
            ...existingCustomVariablePayload,
            chatbot_custom_variable_config_id: option.chatbot_custom_variable_configuration_id,
            chatbot_custom_variable_name: option.chatbot_custom_variable_name,
            chatbot_custom_variable_fallback_value: option.chatbot_custom_variable_fallback_value
        })
    }

    const handleDeleteFieldData = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, option: customVariableDataInterface) => {
        event.stopPropagation();
        setExistingCustomVariablePayload({
            ...existingCustomVariablePayload,
            chatbot_custom_variable_config_id: option.chatbot_custom_variable_configuration_id,
            chatbot_custom_variable_name: option.chatbot_custom_variable_name,
            chatbot_custom_variable_fallback_value: option.chatbot_custom_variable_fallback_value
        })
        if (setFlagDeleteData) {
            setFlagDeleteData(true)
        }
    }

    const getValueOfFields = (type: RetrieveContactFieldType, variable: retrieveContactActionVariableData) => {
        switch (type) {
            case "variable":
                return customVariableList?.custom_variable_data?.find((field: customVariableDataInterface) => field?.chatbot_custom_variable_configuration_id === variable?.chatbot_custom_variable_configuration?.chatbot_custom_variable_configuration_id) || null;
            case "fallback value":
                return variable?.fallback_value ?? '';
            default: 
                return null
        }
    }

    const onChangeOfFields = ({ type, value, variable }: RetrieveContactFieldUpdateType) => {
        onChangeCurrentActiveNodeConfig(nodes, currentActiveNodeConfig, stepAction.chatbot_step_action_id,
            {
                type: type === "variable" ? "flag_change_variable" : "flag_change_fallback_value",
                action_retrieve_contact_variable_data_id: variable?.action_retrieve_contact_variable_data_id,
                ...(type === "variable"
                    ? { chatbot_custom_variable_configuration: value ?? null }
                    : { fallback_value: value ?? '' }
                ),
            },
            {
                updateType: "editExistingStepAction"
            }
        );
    }

    const renderVariableOption = (props: React.HTMLAttributes<HTMLLIElement>, option: customVariableDataInterface) => {
        return (
            <RenderCustomOption
                autocompleteRenderOptionProps={props}
                option={option}
                customOptionName={"New Variable"}
                optionName={option['chatbot_custom_variable_name']}
                optionLabelKey={'chatbot_custom_variable_configuration_id'}
                onClickOfCustomOption={() => setFlagDataCollectionPopup(true)}
                flagShowEditOptionIcon={true}
                onClickOfEditOption={(e: React.MouseEvent<HTMLDivElement, MouseEvent>, option: customVariableDataInterface) => handleEditFieldData(e, option)}
                flagShowDeleteOptionIcon={true}
                onClickOfDeleteOption={(e: React.MouseEvent<HTMLDivElement, MouseEvent>, option: customVariableDataInterface) => handleDeleteFieldData(e, option)}
            />
        );
    }

    return (
        <>
            <Grid container className={clsx(globalStyles.displayFlexStyle)} gap={"12px"}>
                <Grid item xs={12}>
                    <Typography className={popupStyles.popupFieldTypography}>
                        Contact Field <Required />
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <TableContainer className={clsx(globalStyles.WritewizGlobaltable)} sx={{ maxHeight: "450px" }}>
                        <Table aria-label="retrieve contact variable config table" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={clsx(dashboardStyles.tableBodyHeadingFontStyle)}>Contact Field Name</TableCell>
                                    <TableCell className={clsx(dashboardStyles.tableBodyHeadingFontStyle)}>Variable</TableCell>
                                    <TableCell className={clsx(dashboardStyles.tableBodyHeadingFontStyle)}>Fallback Value</TableCell>
                                </TableRow>
                            </TableHead> 
                            <TableBody>
                                {(actionData?.action_retrieve_contact_variable_data && actionData?.action_retrieve_contact_variable_data?.length > 0) ? 
                                    actionData?.action_retrieve_contact_variable_data?.map((variable: retrieveContactActionVariableData) => {
                                        return (
                                            <TableRow className={clsx(dashboardStyles.cellRow)} key={variable?.action_retrieve_contact_variable_data_id}>
                                                <TableCell className={clsx(dashboardStyles.tableBodySubHeadingFontStyle)}>
                                                    {variable?.contact_field_name}
                                                </TableCell>
                                                <TableCell className={clsx(dashboardStyles.tableBodySubHeadingFontStyle, styles.retrieveContactVariableFieldMinWidth)}>
                                                    <Autocomplete
                                                        className={clsx(autoCompleteStyles.contactFilterDropdownsInPopup)}
                                                        options={customVariableList?.custom_variable_data ? [...customVariableList?.custom_variable_data.filter((data) => data.flag_archived == false), emptyNewVariableFieldObj] : []}
                                                        getOptionLabel={(option) => option.chatbot_custom_variable_name || ''}
                                                        value={getValueOfFields("variable", variable) as customVariableDataInterface | null}
                                                        onChange={(_: React.SyntheticEvent, value: customVariableDataInterface | null) => onChangeOfFields({ type: "variable", value, variable })}
                                                        filterOptions={(options, state) => filterAutocompleteOptionsWithException(options, state, "chatbot_custom_variable_name", "chatbot_custom_variable_configuration_id")}
                                                        ListboxProps={{ className: autoCompleteStyles.singleContactListbox }}
                                                        disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                                        renderOption={renderVariableOption}
                                                        renderInput={params => {
                                                            const { InputProps, ...restParams } = params;
                                                            const { startAdornment, ...restInputProps } = InputProps;
                                                            return (
                                                                <TextField
                                                                    {...restParams}
                                                                    placeholder='Select Variable'
                                                                    InputProps={{
                                                                        ...restInputProps,
                                                                        startAdornment: (
                                                                            <div style={{
                                                                                maxHeight: "90px",
                                                                                overflowY: 'auto',
                                                                            }}
                                                                            >
                                                                                {startAdornment}
                                                                            </div>
                                                                        ),
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell className={clsx(dashboardStyles.tableBodySubHeadingFontStyle)}>
                                                    <GlobalTooltip
                                                        title={!getValueOfFields("variable", variable) ? 'Please select a variable before entering fallback value.' : ''}
                                                        placement='top'
                                                        arrow
                                                    >
                                                        <TextField
                                                            disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow) || !getValueOfFields("variable", variable)}
                                                            value={getValueOfFields("fallback value", variable)}
                                                            placeholder='Enter Fallback Value'
                                                            onChange={(e) => onChangeOfFields({ type: "fallback value", value: e.target.value, variable })}
                                                            className={clsx(whatsAppCampaignStyles.whatsappCampaignTextField, globalStyles.fullwidth, styles.retrieveContactVariableFieldMinWidth)}
                                                        />
                                                    </GlobalTooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                    :
                                    <TableRow className={clsx(dashboardStyles.cellRow)}>
                                        <TableCell colSpan={12} className={clsx(globalStyles.textCenter)}>
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </>
    )
}

export default RetrieveContactVariableConfig
